import { styled } from '@linaria/react'
import React, { type FC, useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import { useNavigate } from 'react-router'
import SvgPlus from '../svgr/Plus'
import { TagList } from '../ui/TagList'
import { Boxes } from '../components/Boxes'
import { colors } from '../styles/colors'
import { Link } from 'react-router'
import { createMoneyPoolPagePath } from './CreateMoneyPoolPage'
import type { BoxStatus } from '../api/boxApi'
import { useOnboardingContext } from '../contexts/OnboardingContext'
import { onboardingPagePath } from './OnboardingPage'
import { useAppContext } from '../contexts/App.context'
import { css } from '@linaria/core'

export const indexPagePath = '/'
export const indexMyPagePath = '/my'
export const indexMyVotesPagePath = '/my-votes'
export const indexFinishedPagePath = '/finished'

const allTags = [
  {
    id: 'all',
    to: indexPagePath,
    label: <FormattedMessage id="all" defaultMessage="Все" />,
  } as const,

  {
    id: 'my',
    to: indexMyPagePath,
    label: <FormattedMessage id="my" defaultMessage="Мои" />,
    requiresAuth: true,
  } as const,

  {
    id: 'my-votes',
    to: indexMyVotesPagePath,
    label: <FormattedMessage id="my-votes" defaultMessage="Мои голоса" />,
    requiresAuth: true,
  } as const,
  {
    id: 'finished',
    to: indexFinishedPagePath,
    label: <FormattedMessage id="finished" defaultMessage="Законченные" />,
  } as const,
]

export type TagId = (typeof allTags)[number]['id']

export const IndexPage: FC<{ activeTag: TagId }> = ({ activeTag }) => {
  const navigate = useNavigate()
  const { isCompleted: isOnboardingCompleted } = useOnboardingContext()
  const { authenticated, language } = useAppContext()

  // Filter tags based on authentication status
  const tags = allTags.filter(tag => !tag.requiresAuth || authenticated)

  // Check if onboarding should be shown when user lands on IndexPage
  useEffect(() => {
    if (isOnboardingCompleted === false) {
      navigate(onboardingPagePath)
    }
  }, [isOnboardingCompleted, navigate])

  // Note: Box list analytics are tracked in useTrackBoxListViewed hook
  // called from ScrollBoxes component with actual box count

  // Redirect to index if user loses authentication and is on a protected tag
  useEffect(() => {
    const currentTag = allTags.find(tag => tag.id === activeTag)
    if (currentTag?.requiresAuth && !authenticated) {
      navigate(indexPagePath, { replace: true })
    }
  }, [activeTag, authenticated, navigate])

  const getBoxesStatuses = (): BoxStatus => {
    switch (activeTag) {
      case 'all':
        return 'active'
      case 'my':
        return 'active'
      case 'finished':
        return 'finished'
      case 'my-votes':
        return 'all'
      default:
        return 'all'
    }
  }

  return (
    <IndexPageContainer>
      <IndexPageInner>
        <TagList tags={tags} activeTagId={activeTag} />

        <Boxes
          titleElement={null}
          tabsElement={null}
          status={getBoxesStatuses()}
          votedByMe={activeTag === 'my-votes'}
          createdByMe={activeTag === 'my'}
          lang={language}
          filterType={activeTag}
        />
      </IndexPageInner>

      <Link to={createMoneyPoolPagePath} className={createBoxLinkCss}>
        <CreateMoneyPoolButton>
          <SvgPlus />
        </CreateMoneyPoolButton>
      </Link>
    </IndexPageContainer>
  )
}

const createBoxLinkCss = css`
  display: contents;
`

const IndexPageContainer = styled.div`
  height: 100%;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  height: 100%;
  flex: 1;
`

const IndexPageInner = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: 12px;
  flex: 1;
  min-height: 0;
`

const CreateMoneyPoolButton = styled.button`
  position: sticky;
  bottom: calc(90px + env(safe-area-inset-bottom));
  align-self: flex-end;
  margin-right: 0;

  width: 48px;
  height: 48px;

  background: ${colors.white};
  border-radius: ${colors.radiusCard};
  border: 1px solid ${colors.borderCard};
  box-shadow: ${colors.shadowFab};

  cursor: pointer;
  z-index: 1;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;

  display: flex;
  align-items: center;
  justify-content: center;

  &:active {
    transform: scale(0.92);
    box-shadow: ${colors.shadowMd};
  }

  svg {
    width: 20px;
    height: 20px;
    color: ${colors.secondary};
  }
`
